import fetch from 'isomorphic-fetch'

const DEFAULT_REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_API_REQUEST_TIMEOUT_MS) || 60000

function createHeaders(headers) {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...headers,
  }
}

export interface IHttpError {
  message: string
  statusCode: number
}
class HttpError extends Error {
  statusCode: number

  constructor(message: string, statusCode: number) {
    super(message)
    this.statusCode = statusCode
    this.name = 'HttpError'
  }
}

function formatErrorPart(part: unknown): string {
  if (part == null) return ''
  if (typeof part === 'string') return part
  if (typeof part === 'number' || typeof part === 'boolean') return String(part)
  if (typeof part === 'object') {
    const obj = part as Record<string, unknown>
    if (typeof obj.detail === 'string') return obj.detail
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.msg === 'string') return obj.msg
    if (typeof obj.error === 'string') return obj.error
  }
  try {
    return JSON.stringify(part)
  } catch {
    return 'Неизвестная ошибка'
  }
}

function extractErrorMessage(result: unknown, statusCode: number): string {
  if (statusCode === 404) {
    const fromBody = extractErrorMessageFromBody(result)
    return fromBody || 'Не найдено'
  }
  return extractErrorMessageFromBody(result) || 'Неизвестная ошибка'
}

function extractErrorMessageFromBody(result: unknown): string {
  if (result == null || typeof result !== 'object') {
    return typeof result === 'string' && result.trim() ? result.trim() : ''
  }

  const body = result as Record<string, unknown>
  const candidates = [body.errors, body.error, body.message, body.incompleteWorks]

  for (const candidate of candidates) {
    if (candidate == null) continue
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
    if (Array.isArray(candidate)) {
      const joined = candidate.map(formatErrorPart).filter(Boolean).join(', ')
      if (joined) return joined
    }
    if (typeof candidate === 'object') {
      const formatted = formatErrorPart(candidate)
      if (formatted && formatted !== '{}') return formatted
    }
  }

  return ''
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) {
    return null
  }

  try {
    return JSON.parse(text)
  } catch {
    // HTML / plain text / пустой JSON — не падаем на SyntaxError
    return { message: text.trim().slice(0, 300) || undefined }
  }
}

/**
 * @todo Add OAuth Bearer authenficiation support
 *
 * @param path Example: `/api/session` or `/session`
 * @param options
 * @param localError
 */
async function fetchAPI(
  path: string,
  options: any = {},
  localError = false,
): Promise<Record<string, any>> {
  const {
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    signal: externalSignal,
    ...requestOptions
  } = options
  const controller = new AbortController()
  const abortRequest = () => controller.abort()
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  if (externalSignal?.aborted) {
    controller.abort()
  } else {
    externalSignal?.addEventListener('abort', abortRequest, { once: true })
  }

  if (timeoutMs > 0) {
    timeoutId = setTimeout(abortRequest, timeoutMs)
  }

  try {
    const response = await fetch(path, {
      credentials: 'include',
      ...requestOptions,
      signal: controller.signal,
      headers: createHeaders(requestOptions.headers),
    })

    const result = await readResponseBody(response)

    if (!response.ok) {
      const statusCode = response.status
      const message = extractErrorMessage(result, statusCode)

      if (localError) {
        return {
          error: {
            statusCode,
            message,
          },
        }
      }
      if (statusCode === 400 && result && typeof result === 'object' && (result as any).errors) {
        return result as Record<string, any>
      }
      if (
        statusCode === 400 &&
        result &&
        typeof result === 'object' &&
        ((result as any).error || (result as any).incompleteWorks)
      ) {
        return result as Record<string, any>
      }
      // 409 STAGE_CONFLICT и подобные — отдаём body, чтобы UI мог обновить карточку
      if (
        statusCode === 409 &&
        result &&
        typeof result === 'object' &&
        ((result as any).errors || (result as any).error)
      ) {
        return result as Record<string, any>
      }

      throw new HttpError(message, statusCode)
    }

    if (result == null) {
      if (localError) {
        return {
          error: {
            message: 'Ответ не получен',
          },
        }
      }
      throw new Error('Empty response received')
    }

    return result as Record<string, any>
  } catch (e) {
    console.log({ localError, e })
    if (e instanceof HttpError) {
      if (localError) {
        return {
          error: {
            statusCode: e.statusCode,
            message: e.message,
          },
        }
      }
      throw e
    }
    if (localError) {
      return {
        error: {
          message:
            e instanceof Error && e.name === 'AbortError'
              ? 'Превышено время ожидания ответа'
              : 'Ответ не получен, повторите попытку позже',
        },
      }
    } else {
      console.error(e)
      throw e
    }
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    externalSignal?.removeEventListener('abort', abortRequest)
  }
}

export default fetchAPI
